/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ComponentProps, type CSSProperties, forwardRef } from 'react';
import { cn } from '@/lib/utils';
import { type MaterialSymbolName } from './glyphs';

/**
 * SRAM icon — Material Symbols Sharp (CRCB2B-632).
 *
 * Renders a glyph from the self-hosted, subsetted Material Symbols Sharp font
 * via ligature. The SRAM design default is **weight 300, fill 1** (explicitly
 * directed on the ticket; this intentionally overrides Figma's per-size weight
 * examples — see docs/README-STYLEGUIDE.md#icons). Weight is fixed at 300;
 * `fill` and `size` are adjustable per instance.
 *
 * Accessibility: decorative by default (`aria-hidden`). Pass `aria-label` (or
 * `title`) to expose the icon to assistive tech as an image.
 *
 * New/edited code uses this component; existing `lucide-react` icons coexist
 * and migrate incrementally (README-STYLEGUIDE.md#icons).
 *
 * Renders `data-slot="icon"` so component icon rules can target it. Primitives
 * inherited from shadcn scope their icon rules to `svg`, which silently misses
 * the `<span>` this renders — no error, just an unshrunk glyph and padding that
 * never adjusts. Style icons via `:is(svg, [data-slot='icon'])` (CRCB2B-643).
 */
interface IconProps extends Omit<ComponentProps<'span'>, 'children'> {
    /** Glyph name from the shipped subset manifest (glyphs.ts). */
    name: MaterialSymbolName;
    /**
     * Optical size in px → inline font-size + opsz axis.
     *
     * OMIT IT unless the size is genuinely per-instance. Left unset, the glyph
     * inherits its size from CSS — the `.material-symbols-sharp` 24px default, or
     * whatever a parent sets on `[data-slot='icon']` — which is what lets Button
     * and other primitives drive their own icon scale. Passing `size` writes an
     * INLINE font-size that outranks every class, so it opts the icon out of the
     * containing component's size axis (CRCB2B-643 task 2.4a).
     */
    size?: number;
    /** Filled (1, SRAM default) vs outlined (0). */
    fill?: boolean;
    /** Accessible label; when set the icon is exposed as role="img". */
    'aria-label'?: string;
}

const WEIGHT = 300; // SRAM explicit default; centralized so a future change is one edit.

const Icon = forwardRef<HTMLSpanElement, IconProps>(
    ({ name, size, fill = true, className, style, 'aria-label': ariaLabel, ...props }, ref) => {
        const decorative = ariaLabel == null;
        // Only emit inline sizing when the caller asked for a specific size. An
        // inline font-size beats any class, so emitting one unconditionally is
        // what made the button's per-size icon rules unreachable (task 2.4a).
        const sized: CSSProperties =
            size == null
                ? // No 'opsz' — omitting it is what lets font-optical-sizing track the
                  // inherited font-size. Inline font-variation-settings REPLACES the
                  // class's declaration outright, so 'GRAD' has to be restated here.
                  { fontVariationSettings: `'FILL' ${fill ? 1 : 0}, 'wght' ${WEIGHT}, 'GRAD' 0` }
                : {
                      fontSize: `${size}px`,
                      fontVariationSettings: `'FILL' ${fill ? 1 : 0}, 'wght' ${WEIGHT}, 'GRAD' 0, 'opsz' ${size}`,
                  };
        return (
            <span
                ref={ref}
                className={cn('material-symbols-sharp', className)}
                aria-hidden={decorative || undefined}
                aria-label={ariaLabel}
                role={decorative ? undefined : 'img'}
                style={{ ...sized, ...style }}
                {...props}
                // AFTER the spread, deliberately: `data-slot="icon"` is a contract,
                // not a default. Every component icon rule in the codebase selects on
                // it, so a caller passing its own `data-slot` used to silently remove
                // the icon from all of them — which is what `native-select.tsx` was
                // doing to its chevron. Use `data-part` to name a specific icon.
                data-slot="icon">
                {name}
            </span>
        );
    }
);

Icon.displayName = 'Icon';

export { Icon };
export type { IconProps };
